import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import type { MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalContent from 'LuxKit/Modal/ModalContent'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import LoyaltyEsimUpsellOption from 'loyalty/components/LoyaltyEsimUpsellOption'
import {
  ESIM_UPSELL_TICKET_METADATA,
  type LoyaltyEsimUpsellKind,
} from 'loyalty/constants/loyaltyEsimUpsellOptions'
import { isEsimUpsellTicketId } from 'loyalty/lib/esims/esimUpsellTicketUtils'
import {
  buildEsimUpsellPackageRows,
} from 'loyalty/lib/esims/buildEsimUpsellPackageRows'
import { generateLoyaltyPointsCalculatorExperienceOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { arrayToObject } from 'lib/array/arrayUtils'
import { onlyDefinedProperties } from 'lib/object/objectUtils'
import { getLoyaltyEsimUpsellCartSelection } from 'loyalty/lib/esims/esimCheckoutUtils'
import type { LoyaltyOrderEsimIncludedBenefit } from 'loyalty/hooks/useLoyaltyOrderViewEsimData'

interface LoyaltyEsimUpsellPurchaseSelection {
  kind: 'purchase'
  experience: App.ExperienceOffer
  selectedTicket: App.LoyaltyEsimUpsellCatalogTicket
  esimOfferId: string
  countryCode: string
  currentPackage: App.LoyaltyEsimUpsellTicketId
}

interface LoyaltyEsimUpsellRemoveSelection {
  kind: 'removeUpsell'
  esimOfferId: string
}

export type LoyaltyEsimUpsellModalResult =
  | LoyaltyEsimUpsellPurchaseSelection
  | LoyaltyEsimUpsellRemoveSelection

interface Props {
  catalog: App.LoyaltyEsimUpsellCatalog
  countryCode: string
  /** Hydrated by the row before the modal opens; required for purchase selections. */
  experience?: App.ExperienceOffer
  includedBenefit: LoyaltyOrderEsimIncludedBenefit
  currentPackageTicketId: App.LoyaltyEsimUpsellTicketId
  checkoutMode?: boolean
}

function LoyaltyEsimUpsellModal({
  catalog,
  countryCode,
  experience,
  includedBenefit,
  currentPackageTicketId: currentPackageTicketIdProp,
  checkoutMode = false,
}: Props) {
  const { includedGb, tier: includedTier, isHistoricalTier: isHistoricalIncludedTier } = includedBenefit
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  // Cart
  const cartSelection = useAppSelector((state) => (
    checkoutMode ? getLoyaltyEsimUpsellCartSelection(state.checkout.cart.items) : undefined
  ))
  const cartSelectedTicketId = cartSelection?.selectedPackageTicketId ?? null
  const currentPackageTicketId = cartSelection?.baselinePackageTicketId ?? currentPackageTicketIdProp

  const { allPackageRows, purchasableRows, fixedDataRows, unlimitedDataRows } = useMemo(
    () => buildEsimUpsellPackageRows(intl, catalog, {
      currentPackageTicketId,
    }),
    [catalog, currentPackageTicketId, intl],
  )

  const [selectedTicketId, setSelectedTicketId] = useState<App.LoyaltyEsimUpsellTicketId | null>(
    () => cartSelectedTicketId,
  )
  const [activeTabKind, setActiveTabKind] = useState<LoyaltyEsimUpsellKind>(() => {
    const ticketId = cartSelectedTicketId ?? currentPackageTicketIdProp
    return ticketId
      ? ESIM_UPSELL_TICKET_METADATA[ticketId]?.kind ?? 'gb_pack'
      : 'gb_pack'
  })

  const rowsForActiveTab = activeTabKind === 'gb_pack' ? fixedDataRows : unlimitedDataRows
  const showDataTypeTabSelection = fixedDataRows.length > 0 && unlimitedDataRows.length > 0

  const purchasableRowEarnRequests = useMemo(
    () => purchasableRows.flatMap(row => {
      const request = generateLoyaltyPointsCalculatorExperienceOptions(undefined, {
        offerId: catalog.esimOfferId,
        price: row.addonPrice,
        memberPrice: row.ticket?.memberPrice,
      })
      return request ? [{ row, request }] : []
    }),
    [catalog.esimOfferId, purchasableRows],
  )

  const earnCalculationRequests = useMemo(
    () => purchasableRowEarnRequests.map(({ request }) => request),
    [purchasableRowEarnRequests],
  )
  const { pointsCalculations } = useLoyaltyPointsEarnCalculator(earnCalculationRequests, { useBatch: true })

  const loyaltyEarnByTicketId = useMemo(
    () => onlyDefinedProperties(arrayToObject(
      purchasableRowEarnRequests,
      ({ row }) => row.ticketId,
      (_entry, index) => {
        const calculation = pointsCalculations[index]?.data
        if (!calculation) return undefined
        return {
          earnPoints: Math.max(0, Math.round((calculation.basePoints ?? 0) + (calculation.bonusPoints ?? 0))),
          pointsEarnRatio: calculation.roundedPointsEarnRatio ?? 0,
        }
      },
    )),
    [purchasableRowEarnRequests, pointsCalculations],
  )

  const defaultSelectedTicketId = useMemo(() => {
    const preferredIds = [cartSelectedTicketId, currentPackageTicketId]
    const preferred = preferredIds.find(
      id => id && allPackageRows.some(row => row.ticketId === id),
    )
    return preferred ?? rowsForActiveTab[0]?.ticketId ?? null
  }, [cartSelectedTicketId, currentPackageTicketId, allPackageRows, rowsForActiveTab])

  // Re-sync when rows change and the current selection is no longer valid (e.g. tab switch, catalog refresh).
  useLayoutEffect(() => {
    if (selectedTicketId && allPackageRows.some(row => row.ticketId === selectedTicketId)) {
      return
    }
    setSelectedTicketId(defaultSelectedTicketId)
  }, [defaultSelectedTicketId, allPackageRows, selectedTicketId])

  const onSelectPackage = useCallback((ticketId: App.LoyaltyEsimUpsellTicketId) => {
    setSelectedTicketId(ticketId)
  }, [])

  const onTabChange = useCallback((kind: LoyaltyEsimUpsellKind) => {
    setActiveTabKind(kind)
    const rowsForTab = kind === 'gb_pack' ? fixedDataRows : unlimitedDataRows
    setSelectedTicketId((previousTicketId) => {
      // Keep current selection if still on current tab
      if (previousTicketId && rowsForTab.some(row => row.ticketId === previousTicketId)) {
        return previousTicketId
      }
      // Prefer cart upsell if it's still on current tab
      if (cartSelectedTicketId && rowsForTab.some(row => row.ticketId === cartSelectedTicketId)) {
        return cartSelectedTicketId
      }
      // Otherwise, first row on tab
      return rowsForTab[0]?.ticketId ?? null
    })
  }, [cartSelectedTicketId, fixedDataRows, unlimitedDataRows])

  const onFixedDataTabClick = useCallback(() => {
    onTabChange('gb_pack')
  }, [onTabChange])

  const onUnlimitedDataTabClick = useCallback(() => {
    onTabChange('unlimited_days')
  }, [onTabChange])

  const onClose = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const onContinue = useCallback((_event: MouseEvent<HTMLButtonElement>, resolve: (result?: unknown) => void) => {
    if (!selectedTicketId) {
      resolve()
      return
    }

    if (selectedTicketId === currentPackageTicketId) {
      if (!cartSelectedTicketId) {
        resolve()
        return
      }
      const payload: LoyaltyEsimUpsellModalResult = {
        kind: 'removeUpsell',
        esimOfferId: catalog.esimOfferId,
      }
      resolve(payload)
      return
    }

    const selectedRow = allPackageRows.find(row => row.ticketId === selectedTicketId)
    if (!selectedRow?.ticket) {
      resolve()
      return
    }

    const selectedCurrentPackage = selectedRow.ticket.bookingOptions?.fromESimDataPackage
    if (!selectedCurrentPackage || !isEsimUpsellTicketId(selectedCurrentPackage)) {
      resolve()
      return
    }

    if (!experience) {
      resolve()
      return
    }

    const payload: LoyaltyEsimUpsellModalResult = {
      kind: 'purchase',
      experience,
      selectedTicket: selectedRow.ticket,
      esimOfferId: catalog.esimOfferId,
      countryCode,
      currentPackage: selectedCurrentPackage,
    }
    resolve(payload)
  }, [
    cartSelectedTicketId, catalog, countryCode, currentPackageTicketId, allPackageRows, experience, selectedTicketId,
  ])

  const isRemoveUpsellSelection = !!selectedTicketId
    && !!cartSelectedTicketId
    && selectedTicketId === currentPackageTicketId

  const isPurchaseSelection = !!selectedTicketId && (
    cartSelectedTicketId
      ? selectedTicketId !== cartSelectedTicketId
      : selectedTicketId !== currentPackageTicketId
  )

  const canContinue = isRemoveUpsellSelection || (isPurchaseSelection && !!experience)

  const optionsForActiveTab = useMemo(
    () => rowsForActiveTab.map((row) => {
      const loyaltyPoints = loyaltyEarnByTicketId[row.ticketId]
      return {
        ticketId: row.ticketId,
        title: row.title,
        description: row.description,
        badge: row.badge,
        isCurrent: row.isCurrent,
        addonPrice: row.addonPrice,
        earnPoints: loyaltyPoints?.earnPoints,
        pointsEarnRatio: loyaltyPoints?.pointsEarnRatio,
      }
    }),
    [loyaltyEarnByTicketId, rowsForActiveTab],
  )

  const title = intl.formatMessage({
    id: '_Byg2NHd9oPoDSbS6fQ6',
    defaultMessage: 'Add more data to your eSIM',
    description: 'Title shown at the top of the modal where members choose a paid eSIM data upgrade.',
  })

  const subtitle = intl.formatMessage({
    id: '2WdOD0r27yCK7nO8woLk',
    defaultMessage: 'Get set before you travel',
    description: 'Short subtitle shown under the eSIM upgrade modal title.',
  })

  const continueLabel = intl.formatMessage({
    id: '7HuYbVxMw-v_dfnPFGt7',
    defaultMessage: 'Continue',
    description: 'Primary button to confirm eSIM data upgrade selection',
  })

  const totalSuffix = intl.formatMessage({
    id: 'vHsLYfIBvLjENOFCWOJy',
    defaultMessage: 'total',
    description: 'Lowercase suffix after upgrade price meaning total add-on cost',
  })

  const plusLabel = intl.formatMessage({
    id: 'FF6Mnk9oN5ZE4BzEyhMc',
    defaultMessage: '+',
    description: 'Plus symbol shown before the extra amount charged for a paid eSIM upgrade option.',
  })

  const includedBannerTitle = isHistoricalIncludedTier ? intl.formatMessage(
    {
      id: 'ipWm7cCjHQWtV-mNEFws',
      defaultMessage: '{gb}GB added when you were {tierName}',
      description: 'Title in the eSIM upgrade modal when the included data came from the member’s older tier',
    },
    { gb: includedGb, tierName: getLoyaltyTierDisplayName(includedTier) },
  ) : intl.formatMessage(
    {
      id: '_-ANg7lq7AJ22TuUJ2rE',
      defaultMessage: '{gb}GB included with your tier',
      description: 'Title in the banner showing how much data the member already gets included from their loyalty tier',
    },
    { gb: includedGb },
  )

  const includedBannerSubtitle = intl.formatMessage({
    id: '0Asq1Ep66UTeN7vN_fiM',
    defaultMessage: 'Need more? Choose a top-up below',
    description: 'Subtitle under the included-data banner inviting the member to pick a top-up',
  })

  const fixedDataTabLabel = intl.formatMessage({
    id: 'XT2KfDTP-2foA439f_M4',
    defaultMessage: 'Fixed data',
    description: 'Tab label for fixed-amount eSIM data upgrade options',
  })

  const unlimitedDataTabLabel = intl.formatMessage({
    id: 'J-4PzH7Eyi02294_P0pH',
    defaultMessage: 'Unlimited data',
    description: 'Tab label for time-limited unlimited eSIM data upgrade options',
  })

  const currentPackagePriceLabel = intl.formatMessage({
    id: '42nu0B-FCMnu9OoSEkGX',
    defaultMessage: 'Current',
    description: 'Price label for the currently selected included eSIM package option',
  })

  return (
    <ModalBase size="S" onClose={onClose}>
      <ModalHeader title={title} subtitle={subtitle} onCloseButtonClick={onClose} />
      <ModalBody>
        <ModalContent>
          <Group direction="vertical" gap={12}>
            <Sheet background={`tier-${includedTier}-light`} padding={16}>
              <Group direction="horizontal" verticalAlign="center" gap={12} fullWidth>
                <LoyaltyTierIcon tier={includedTier} size="L" />
                <Group direction="vertical" gap={2} grow>
                  <BodyText variant="medium" weight="bold">
                    {includedBannerTitle}
                  </BodyText>
                  <BodyText variant="medium" colour="inherit">
                    {includedBannerSubtitle}
                  </BodyText>
                </Group>
              </Group>
            </Sheet>
            {showDataTypeTabSelection && (
              <TabButtonsGroup fit="grow">
                <TabButton
                  size="medium"
                  isActive={activeTabKind === 'gb_pack'}
                  onClick={onFixedDataTabClick}
                >
                  {fixedDataTabLabel}
                </TabButton>
                <TabButton
                  size="medium"
                  isActive={activeTabKind === 'unlimited_days'}
                  onClick={onUnlimitedDataTabClick}
                >
                  {unlimitedDataTabLabel}
                </TabButton>
              </TabButtonsGroup>
            )}
            <Group as="fieldset" direction="vertical" gap={20} key={activeTabKind}>
              {optionsForActiveTab.map(option => (
                <LoyaltyEsimUpsellOption
                  key={option.ticketId}
                  option={option}
                  checked={selectedTicketId === option.ticketId}
                  displayTier={displayTier}
                  currentLabel={currentPackagePriceLabel}
                  totalSuffix={totalSuffix}
                  plusLabel={plusLabel}
                  onSelect={onSelectPackage}
                />
              ))}
            </Group>
          </Group>
        </ModalContent>
        <ModalFooter
          primaryActionProps={{
            children: continueLabel,
            onClick: onContinue,
            disabled: !canContinue,
          }}
        />
      </ModalBody>
    </ModalBase>
  )
}

export default LoyaltyEsimUpsellModal
